import { Action, createReducer, on } from '@ngrx/store';
import * as uploadActions from '../actions/upload.actions';
import { EUploadState } from '@app-root-enums';
import { IInformations, IUploadedFile, IUploadSidenav } from '@app-root-interfaces';

export const uploadFeatureKey = 'upload';

export interface State {
    addedFiles: { [key: string]: IUploadedFile };
    uploadSidenav: IUploadSidenav;
    informations: IInformations;
}

export const initialState: State = {
    addedFiles: {},
    uploadSidenav: {
        fileActive: null,
        folderActive: false
    },
    informations: { legals: [], management: [], locations: [], translations: [] }
};

const uploadReducer = createReducer(
    initialState,
    on(uploadActions.addFilesSuccess, uploadActions.updateMultipleFiles, (state, { files }) => ({
        ...state,
        addedFiles: files.reduce((previous, file) => ({ ...previous, [file.tmp_id]: file }), state.addedFiles)
    })),
    on(uploadActions.setActiveFile, (state, { fileId }) => ({
        ...state,
        uploadSidenav: {
            ...state.uploadSidenav,
            fileActive: fileId
        }
    })),
    on(
        uploadActions.updateUploadedFileSuccess,
        uploadActions.updatePreviewSuccess,
        uploadActions.uploadingFilesSuccess,
        uploadActions.addDriveSuccess,
        uploadActions.removeDriveSuccess,
        (state, { values }) => ({
            ...state,
            addedFiles: {
                ...state.addedFiles,
                [values.tmp_id]: values
            }
        })
    ),
    on(uploadActions.addFolderSuccess, (state, { values }) => ({
        ...state,
        addedFiles: {
            ...state.addedFiles,
            [values.tmp_id]: values
        }
    })),
    on(uploadActions.uploadingFilesFailure, (state, { values }) => ({
        ...state,
        addedFiles: {
            ...state.addedFiles,
            [values.tmp_id]: {
                ...state.addedFiles[values.tmp_id],
                uploadState: EUploadState.FAILED
            }
        }
    })),
    on(uploadActions.removeFileFromUploadList, (state, { values }) => {
        const newState = { ...state, addedFiles: { ...state.addedFiles } };
        delete newState.addedFiles[values.tmp_id];
        return newState;
    }),
    on(uploadActions.uploadInformationManagementSuccess, (state, { management }) => ({
        ...state,
        informations: {
            ...state.informations,
            management: [...state.informations.management, ...management]
        }
    })),
    on(uploadActions.uploadInformationLegalsSuccess, (state, { legals }) => ({
        ...state,
        informations: {
            ...state.informations,
            legals: [...state.informations.legals, ...legals]
        }
    })),
    on(uploadActions.uploadInformationLocationsSuccess, (state, { locations }) => ({
        ...state,
        informations: {
            ...state.informations,
            locations: [...state.informations.locations, ...locations]
        }
    })),
    on(uploadActions.uploadInformationTranslationsSuccess, (state, { translations }) => ({
        ...state,
        informations: {
            ...state.informations,
            translations: [...state.informations.translations, ...translations]
        }
    })),
    on(uploadActions.clearInformationManagement, (state) => ({
        ...state,
        informations: {
            ...state.informations,
            management: []
        }
    })),
    on(uploadActions.clearInformationLegals, (state) => ({
        ...state,
        informations: {
            ...state.informations,
            legals: []
        }
    })),
    on(uploadActions.clearInformationLocations, (state) => ({
        ...state,
        informations: {
            ...state.informations,
            locations: []
        }
    })),
    on(uploadActions.clearInformationTranslations, (state) => ({
        ...state,
        informations: {
            ...state.informations,
            translations: []
        }
    })),
    on(uploadActions.submitManagementData, (state) => ({
        ...state,
        informations: {
            ...state.informations,
            managementLoading: true
        }
    })),
    on(uploadActions.submitLegalData, (state) => ({
        ...state,
        informations: {
            ...state.informations,
            legalsLoading: true
        }
    })),
    on(uploadActions.submitLocationData, (state) => ({
        ...state,
        informations: {
            ...state.informations,
            locationsLoading: true
        }
    })),
    on(uploadActions.submitTranslationData, (state) => ({
        ...state,
        informations: {
            ...state.informations,
            translationsLoading: true
        }
    })),
    on(uploadActions.submitManagementDataSuccess, uploadActions.submitManagementDataFailure, (state) => ({
        ...state,
        informations: {
            ...state.informations,
            management: [],
            managementLoading: false
        }
    })),
    on(uploadActions.submitLocationDataSuccess, uploadActions.submitLocationDataFailure, (state) => ({
        ...state,
        informations: {
            ...state.informations,
            locations: [],
            locationsLoading: false
        }
    })),
    on(uploadActions.submitLegalDataSuccess, uploadActions.submitLegalDataFailure, (state) => ({
        ...state,
        informations: {
            ...state.informations,
            legals: [],
            legalsLoading: false
        }
    })),
    on(uploadActions.submitTranslationDataSuccess, uploadActions.submitTranslationDataFailure, (state) => ({
        ...state,
        informations: {
            ...state.informations,
            translations: [],
            translationsLoading: false
        }
    }))
);

export function reducer(state: State | undefined, action: Action) {
    return uploadReducer(state, action);
}
